import { useCallback, useState } from 'react';

import { checkFirstName, checkLastName } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useUpdateProfile } from '../../hooks/update-profile';
import { useVisit } from '../../hooks/visit';

import { PageTitle } from '../page-title';
import { StepActions } from '../step-actions';
import { TextField } from '../text-field';

import styles from './style.module.css';

// Step 1: first and last name, both required and at most 100 characters.
// Back from here signs out, so the button says so. Signing out is a request
// like any other (#99): while it runs neither button can be pressed; when it
// fails, the reason shows under them, what was typed stays, and Sign out
// can be pressed again. On success the router leaves onboarding.
export function NameStep({ profile, onBack, onDone }) {
  const { text, translate } = useLanguage();
  const updateProfile = useUpdateProfile();
  const capture = useVisit();
  const firstName = useField({ initial: profile.firstName, check: checkFirstName });
  const lastName = useField({ initial: profile.lastName, check: checkLastName });
  const [ busy, setBusy ] = useState(false);
  const [ signingOut, setSigningOut ] = useState(false);
  const [ signOutError, setSignOutError ] = useState();

  const signOut = useCallback(function() {
    setSigningOut(true);
    setSignOutError();

    return onBack().catch(function(failed) {
      setSigningOut(false);
      setSignOutError(failed.message);
    });
  }, [ onBack ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ firstName, lastName ]) === false) {
      return null;
    }

    setBusy(true);

    const owns = capture();

    // The browser has run the same rules, so a refusal here is rare; the
    // last-name sentence goes under its own field, anything else under the
    // first.
    return updateProfile({ firstName: firstName.value, lastName: lastName.value }).then(function() {
      if (owns() === false) {
        return null;
      }

      return onDone();
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setBusy(false);

      if (failed.message === 'Enter your last name') {
        return lastName.refuse(failed.message);
      }

      return firstName.refuse(failed.message);
    });
  }, [ firstName, lastName, updateProfile, onDone, capture ]);

  let message = null;

  if (signOutError != null) {
    message = <p className={ styles.error } role='alert'>{ translate(signOutError) }</p>;
  }

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <PageTitle title={ text.nameTitle } intro={ text.nameIntro }/>

      <TextField
        ref={ firstName.ref }
        id='first-name'
        label={ text.firstName }
        value={ firstName.value }
        onChange={ firstName.onChange }
        onBlur={ firstName.onBlur }
        error={ translate(firstName.error) }
        autoComplete='given-name'
      />

      <TextField
        ref={ lastName.ref }
        id='last-name'
        label={ text.lastName }
        value={ lastName.value }
        onChange={ lastName.onChange }
        onBlur={ lastName.onBlur }
        error={ translate(lastName.error) }
        autoComplete='family-name'
      />

      <StepActions backLabel={ text.signOut } onBack={ signOut } backDisabled={ busy || signingOut } submitLabel={ text.continue } busy={ busy || signingOut }/>

      { message }
    </form>
  );
}
